// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — useAchievements HOOK
// ────────────────────────────────────────────────────────────────────
// Subscribes a component to the player's achievement map and returns a
// normalized, SELF-VALIDATED snapshot on every change. The achievement
// twin of useEquippedCosmetics / useEquippedCompanion.
//
// Any screen — Codex, Plaza, the Gallery, a shop gating a cross-game
// radiant — reads achievements with ONE line and can NEVER fall out of
// sync, because every screen reads from this single source.
//
// ── WHAT YOU GET ─────────────────────────────────────────────────────
//   const { map, earnedIds, count, renown, isRewardUnlocked } =
//     useAchievements(uid);
//
//   map        — { [id]: { earnedAt, game, rarity, source } }
//   earnedIds  — string[] of ids the player holds (validated against the
//                registry — stale/renamed ids are filtered out)
//   count      — number of valid earned achievements
//   renown     — total IE renown points
//   isRewardUnlocked(rewardId) — pure cross-game gate check, no Firebase
//
// ── WHY IT'S A FAILSAFE ──────────────────────────────────────────────
//   1. Single source of truth. No screen reads profiles/{uid}/achievements
//      directly — they call this hook. Earn something in Terror, and a
//      shop in Pierre gating on it lights up the next time it reads.
//   2. Self-validating. If a stored id is no longer in the registry
//      (renamed, removed), earnedIds silently drops it — a gallery can
//      never crash on a phantom achievement.
//   3. Null-safe. uid=null returns an empty, safe snapshot with NO
//      Firebase subscription.
//   4. Self-cleaning. Tears down its listener on unmount / uid change.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from "react";
import { listenToAchievements } from "./achievementWrites";
import {
  isValidAchievement,
  totalRenown,
  isRewardUnlocked as gateCheck,
} from "./achievements";

const EMPTY = {};

export function useAchievements(uid) {
  const [map, setMap] = useState(EMPTY);

  useEffect(() => {
    if (!uid) {
      setMap(EMPTY);
      return;
    }
    const unsub = listenToAchievements(uid, (next) => {
      setMap(next || EMPTY);
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [uid]);

  // Self-validation: keep only ids that still exist in the registry.
  const earnedIds = useMemo(
    () => Object.keys(map).filter(isValidAchievement),
    [map]
  );

  const renown = useMemo(() => totalRenown(map), [map]);

  // Pure cross-game gate check bound to this player's map — no Firebase.
  const isRewardUnlocked = useCallback(
    (rewardId) => gateCheck(map, rewardId),
    [map]
  );

  return {
    map,
    earnedIds,
    count: earnedIds.length,
    renown,
    isRewardUnlocked,
  };
}

export default useAchievements;
